// CostOfWork.tsx
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Save, AddCircleOutline, Edit, Delete } from '@mui/icons-material';
import { RootState } from 'src/redux-store/appStore';
import { useDispatch, useSelector } from 'react-redux';
import { updateElectrical } from 'src/redux-store/slices/constructionFormSlice';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { useParams } from 'react-router-dom';

interface WorkItem {
  name?: string;
  Item: string;
  Size?: number;
  check: boolean;
  Cost: number;
  Quantity?: number;
  WallArea?: string | null;
  FenceHeight?: string | null;
  SignType?: string | null;
  SignArea?: string | null;
  PoolItem?: string | null;
  PoolArea?: string | null;
  WallType?: string | null;
  RoofReplacementType?: string | null;
}

interface EstimatedCosts {
  ElectricalWorkCost: number | null;
}

interface CostOfWorkProps {
  estimatedCostOfWork: EstimatedCosts;
  onBack: () => void;
  onNext: () => void;
  townshipID: number;
}

interface ConstructionItem {
  id: number;
  item: string;
  quantity: string | number;
  size: string | number;
  Type: string;
}

const CostOfWork: React.FC<CostOfWorkProps> = ({
  estimatedCostOfWork,
  onBack,
  onNext,
  townshipID
}) => {
  const constructionItemsOptions = [
    'HP Garbage Disposal',
    'HP Motors 1/+ HP',
    'HP/KW Space Heater/Air Handler',
    'KW Elec. Range/Receptacle',
    'KW Elec. Water Heater',
    'KW Elec. Dryer/Receptacle',
    'KW Elec. Sign/Outline Light',
    'KW Oven/Surface Unit',
    'KW Central A/C Unit',
    'KW Dishwasher',
    'KW Baseboard Heat',
    'KW Transformer/Generator',
    'AMP Service',
    'AMP Subpanels',
    'AMP Motor Control Center'
  ];
  const dispatch = useDispatch();
  const {townshipId}= useParams();
  
  const [isEditing, setIsEditing] = useState(false);
  const [electricalEstimatedCostOfWork, setElectricalEstimatedCostOfWork] =
    useState(Number(estimatedCostOfWork));
  const [constructionItems, setConstructionItems] = useState<
    ConstructionItem[]
  >([]);

  // State to track form inputs
  const [formInput, setFormInput] = useState<{
    id: number | null;
    item: string;
    quantity: string | number;
    size: string | number;
    Type: string;
  }>({
    id: null,
    item: '',
    quantity: '',
    size: '',
    Type: ''
  });

  const [otherWorkItemsName, setOtherWorkItemsName] = useState([
    { Item: '', Quantity: 0, Fee: 0, check: true, Type: 'other' }
  ]);

  const workItems =
    useSelector(
      (state: RootState) => state.constructionFormSlice?.Electrical?.WorkItems
    ) || [];

  function getItemType(itemName: string) {
    const str = itemName[0] + itemName[1];
    if (str === 'HP') {
      return 'Motors And Devices';
    } else if (str === 'KW') {
      return 'Transformers And Generators';
    } else if (str === 'AM') {
      return 'Service Panels And Entrances';
    }
  }

  // Add a useEffect to initialize constructionItems from Redux when component mounts
  useEffect(() => {
    if (workItems && workItems.length > 0) {
      // Filter out construction items from workItems
      const savedConstructionItems = workItems
        .filter((item) => item.name === 'construction')
        .map((item, index) => ({
          id: index + 1,
          item: item.Item,
          quantity: item.Quantity || 0,
          size: item.Size || 0,
          Type: item.Type
        }));

      if (savedConstructionItems.length > 0) {
        setConstructionItems(savedConstructionItems);
      }
    }
  }, [workItems]); // Depend on workItems

  // Function to handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormInput({ ...formInput, [name]: value });
  };

  const handleWorkItemChange = (
    index: number,
    field: keyof WorkItem,
    value: string | boolean | number
  ) => {
    const updatedWorkItems = [...workItems];
    updatedWorkItems[index] = { ...updatedWorkItems[index], [field]: value };

    // Update only the nested property
    if (isNaN(Number(value)) || typeof value === 'boolean') {
      dispatch(
        updateElectrical({
          WorkItems: updatedWorkItems
        })
      );
    } else if (Number(value) >= 0) {
      dispatch(
        updateElectrical({
          WorkItems: updatedWorkItems
        })
      );
    }
  };

  // Convert construction items to work items format
  const constructionItemsToWorkItems = (): WorkItem[] => {
    return constructionItems.map((item) => ({
      Item: item.item,
      Quantity: Number(item.quantity),
      Size: Number(item.size),
      Type: item.Type,
      check: true,
      Cost: 0,
      name: 'construction'
    }));
  };

  // Function to add/update construction item
  const handleAddOrUpdateItem = () => {
    if (!formInput.item || !formInput.quantity) {
      toast.error('Please fill in required fields');
      return;
    }

    let updatedConstructionItems: ConstructionItem[];

    if (isEditing && formInput.id !== null) {
      // Update existing item
      updatedConstructionItems = constructionItems.map((item) =>
        item.id === formInput.id ? { ...(formInput as ConstructionItem) } : item
      );
    } else {
      // Add new item
      const newItem: ConstructionItem = {
        id:
          constructionItems.length > 0
            ? Math.max(...constructionItems.map((item) => item.id)) + 1
            : 1,
        item: formInput.item,
        quantity: formInput.quantity,
        size: formInput.size,
        Type: getItemType(formInput.item)
      };
      updatedConstructionItems = [...constructionItems, newItem];
    }

    setConstructionItems(updatedConstructionItems);
    setIsEditing(false);
    setFormInput({ id: null, item: '', quantity: '', size: '', Type: '' }); // Reset form
  };

  // Function to populate form fields for editing
  const handleEditItem = (item: ConstructionItem) => {
    setFormInput({
      id: item.id,
      item: item.item,
      quantity: item.quantity,
      size: item.size,
      Type: item?.Type
    });
    setIsEditing(true);
  };

  // Function to delete an item
  const handleDeleteItem = (id: number) => {
    setConstructionItems(constructionItems.filter((item) => item.id !== id));
  };

  // Function to get all items to save (displayed work items + construction items)
  const getAllItemsForSave = (): WorkItem[] => {
    // Convert construction items to work item format
    const constructionWorkItems = constructionItemsToWorkItems();

    // Filter out any existing construction items from workItems
    const existingNonConstructionWorkItems =
      workItems?.filter((item) => item.name !== 'construction') || [];

    // Combine with existing work items
    return [...existingNonConstructionWorkItems, ...constructionWorkItems];
  };

  async function getElectricalFees() {
    try {
      // First update Redux with all items
      const allWorkItems = getAllItemsForSave();

      // Update Redux before making API call
      dispatch(
        updateElectrical({
          WorkItems: allWorkItems,
          EstimatedCostOfElectricWork: electricalEstimatedCostOfWork
        })
      );

      const payload = {
        UseGroup: 'R-3, R-5',
        CertificateType: 'Clearance',
        EstimatedCostOfElectricWork: electricalEstimatedCostOfWork,
        TownshipID : townshipId || townshipID,
        WorkItems: allWorkItems
      };

      const response = await httpRequest(
        'POST',
        `/construction/electrical/get_electrical_fees`,
        payload
      );

      if (response?.status === 'success') {
        const data = response?.data || '';
        const dataDetails = {
          'Electrical Size': data['Electrical Size'],
          'Certification Cost': data['Certification Cost'],
          totalFee: data.totalFee,
          summary: data.summary
        };
        dispatch(
          updateElectrical({
            getAllFee: dataDetails
          })
        );
        toast.success('Electrical fees calculated successfully');
      } else {
        toast.error(response?.message || 'Failed to get electrical fees');
      }
    } catch (error) {
      toast.error('An error occurred while calculating fees');
      console.error('Error getting electrical fees:', error);
    }
  }

  const handleAddOtherWorkItem = () => {
    if (!otherWorkItemsName[0].Item) {
      toast.error('Please enter an item name');
      return;
    }

    const newOtherWorkItem = {
      Item: otherWorkItemsName[0].Item,
      Quantity: otherWorkItemsName[0].Quantity,
      Fee: otherWorkItemsName[0].Fee,
      check: true,
      Cost: otherWorkItemsName[0].Fee,
      Type: 'other'
    };

    // Update Redux
    dispatch(
      updateElectrical({
        WorkItems: [...workItems, newOtherWorkItem]
      })
    );

    // Reset form
    setOtherWorkItemsName([
      { Item: '', Quantity: 0, Fee: 0, check: true, Type: 'other' }
    ]);

    toast.success('Other item added successfully');
  };

  return (
    <Grid container gap={3}>
      {/* Left Grid - Estimation fields */}
      <Grid item md={3} sm={4}>
        {/* Estimated Cost of Work */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Estimated Cost of Work</Typography>
          <TextField
            fullWidth
            type="number"
            name="NewBuildingCost"
            value={electricalEstimatedCostOfWork}
            onChange={(e) =>
              setElectricalEstimatedCostOfWork(Number(e.target.value))
            }
            onBlur={() =>
              dispatch(
                updateElectrical({
                  EstimatedCostOfElectricWork: electricalEstimatedCostOfWork
                })
              )
            }
            label={
              <span>
                Electrical Work<span style={{ color: 'red' }}>*</span>
              </span>
            }
            variant="outlined"
          />
        </Box>
        <br />
        <br />
        {/* Construction Item */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Construction Items</Typography>

          {/* Form to Add/Update Items */}
          <FormControl fullWidth>
            <InputLabel>Select Item</InputLabel>
            <Select
              name="item"
              label="Select Item"
              value={formInput.item}
              onChange={handleInputChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300 // Controls the dropdown height
                  }
                }
              }}
            >
              {constructionItemsOptions?.map((itemName: string) => (
                <MenuItem key={itemName} value={itemName}>
                  {itemName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            name="quantity"
            label="Quantity"
            type="number"
            fullWidth
            value={formInput.quantity}
            onChange={handleInputChange}
          />

          <TextField
            name="size"
            label="Size"
            fullWidth
            value={formInput.size}
            onChange={handleInputChange}
          />

          <Button
            variant="contained"
            color={isEditing ? 'secondary' : 'primary'}
            startIcon={isEditing ? <Save /> : <AddCircleOutline />}
            onClick={handleAddOrUpdateItem}
          >
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Grid>

      <Divider orientation="vertical" variant="middle" flexItem />

      {/* Right Grid - Work items */}
      <Grid item md={8} sm={6}>
        <Typography variant="h5">Electrical Work items</Typography>
        <br />
        <Grid container spacing={3}>
          {workItems
            ?.filter((item) => item.name !== 'construction') // Hide construction items
            .map((item, index) => (
              <Grid
                item
                xs={12}
                md={4}
                sm={6}
                key={`${item.Item}-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.check}
                      onChange={(e) =>
                        handleWorkItemChange(index, 'check', e.target.checked)
                      }
                    />
                  }
                  label={item.Item}
                />

                {item.check && (
                  <TextField
                    type="number"
                    fullWidth
                    label="Quantity"
                    variant="outlined"
                    value={item.Quantity || 0}
                    onChange={(e) =>
                      handleWorkItemChange(index, 'Quantity', e.target.value)
                    }
                  />
                )}
              </Grid>
            ))}
        </Grid>
        <br />
        <br />

        {/* Add More Items */}
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Typography variant="h5">Add More Items</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              gap: 2
            }}
          >
            <TextField
              name="itemName"
              label="Other item name"
              fullWidth
              value={otherWorkItemsName[0].Item}
              onChange={(e) => {
                const updatedWorkItems = [...otherWorkItemsName];
                updatedWorkItems[0].Item = e.target.value;
                setOtherWorkItemsName(updatedWorkItems);
              }}
            />

            <TextField
              label="Quantity"
              name="Quantity"
              type="number"
              fullWidth
              value={otherWorkItemsName[0].Quantity}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  const updatedWorkItems = [...otherWorkItemsName];
                  updatedWorkItems[0].Quantity = Number(e.target.value);
                  setOtherWorkItemsName(updatedWorkItems);
                }
              }}
            />

            <TextField
              name="Fee"
              label="Fee"
              type="number"
              fullWidth
              value={otherWorkItemsName[0].Fee}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  const updatedWorkItems = [...otherWorkItemsName];
                  updatedWorkItems[0].Fee = Number(e.target.value);
                  setOtherWorkItemsName(updatedWorkItems);
                }
              }}
            />

            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleAddOtherWorkItem}
              fullWidth
            >
              Add Items
            </Button>
          </Box>
        </Grid>

        {/* Table to show added construction items */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5">Selected Construction Items</Typography>
          <br />
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      borderBottom: '2px solid #ddd'
                    }}
                  >
                    Item
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      borderBottom: '2px solid #ddd'
                    }}
                  >
                    Quantity
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      borderBottom: '2px solid #ddd'
                    }}
                  >
                    Size
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      borderBottom: '2px solid #ddd',
                      textAlign: 'center'
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {constructionItems.length > 0 ? (
                  constructionItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.item}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.size}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          color="primary"
                          onClick={() => handleEditItem(item)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No electrical items added yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>

      {/* Form Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          width: '100%'
        }}
      >
        <Button onClick={onBack} variant="contained" color="inherit">
          Back
        </Button>
        <Button
          onClick={async () => {
            await getElectricalFees();
            onNext();
          }}
          variant="contained"
        >
          Save
        </Button>
      </Box>
    </Grid>
  );
};

export default CostOfWork;
